<template>
  <div
    class="language-switcher-container"
    v-if="supportedLocales.length > 1"
    ref="switcherContainer"
  >
    <!-- Button that displays the current language and opens the dropdown -->
    <button
      @click="toggleDropdown"
      :disabled="loading"
      class="language-switcher-button"
      :class="{ 'dropdown-open': isDropdownOpen }"
      aria-haspopup="true"
      :aria-expanded="isDropdownOpen"
    >
      <span class="selected-language">
        <span class="flag" :class="currentLocaleObject.flagClass"></span>
        <span v-if="viewLabel" class="language-label">{{
          currentLocaleObject.label
        }}</span>
      </span>
      <svg
        class="dropdown-arrow"
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>

    <!-- Language List (Dropdown) -->
    <transition name="fade">
      <ul v-if="isDropdownOpen" class="language-options-list">
        <li
          v-for="locale in supportedLocales"
          :key="locale.value"
          @click="onLanguageSelected(locale.value)"
          class="language-option"
          :class="{ 'is-selected': locale.value === currentLanguage }"
          role="option"
        >
          <span class="flag" :class="locale.flagClass"></span>
          <span class="language-label">{{ locale.label }}</span>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
import LocalStorage from '@/services/LocalStorage';
import UserService from '@/services/AccountHttp';

export default {
  props: {
    viewLabel: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentLanguage: this.$i18n.locale,
      supportedLocales: [],
      loading: false,
      isDropdownOpen: false, // Controls dropdown visibility
    };
  },
  watch: {
    // Watch for changes in the dropdown's open state to add/remove a global click listener.
    isDropdownOpen(isOpen) {
      if (isOpen) {
        // When the dropdown opens, add a listener to the document.
        document.addEventListener('click', this.handleClickOutside);
      } else {
        // When it closes, remove the listener.
        document.removeEventListener('click', this.handleClickOutside);
      }
    },
  },
  computed: {
    // Returns the full object for the current locale for easy access in the template
    currentLocaleObject() {
      const defaultLocale = {
        value: 'pt-BR',
        label: 'Português',
        flagClass: 'flag__br',
      };
      return (
        this.supportedLocales.find((l) => l.value === this.currentLanguage) ||
        defaultLocale
      );
    },
  },
  methods: {
    toggleDropdown() {
      if (!this.loading) {
        this.isDropdownOpen = !this.isDropdownOpen;
      }
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    // This method is called by the global event listener.
    handleClickOutside(event) {
      // If the component's element exists and the click was outside of it...
      if (
        this.$refs.switcherContainer &&
        !this.$refs.switcherContainer.contains(event.target)
      ) {
        // ...close the dropdown.
        this.closeDropdown();
      }
    },
    async onLanguageSelected(newLanguage) {
      if (newLanguage === this.currentLanguage) {
        this.closeDropdown();
        return;
      }

      this.loading = true;
      this.closeDropdown();

      this.currentLanguage = newLanguage;
      this.$i18n.locale = newLanguage;
      LocalStorage.set('language', newLanguage);
      this.$emit('change', newLanguage);

      try {
        const result = await UserService.setErrorHandler((error) => {
          throw error;
        }).updateLanguage(newLanguage);

        if (result) {
          console.log('Updated language in backend:', newLanguage);
        }
      } catch (error) {
        console.error('Failed to update language in backend:', error);
      } finally {
        this.loading = false;
        window.location.reload();
      }
    },
    async loadStoredLanguage() {
      const storedLanguage = LocalStorage.get('language');
      const defaultLanguage = process.env.VUE_APP_I18N_LOCALE ?? 'pt-BR';

      if (storedLanguage) {
        this.currentLanguage = storedLanguage;
        this.$i18n.locale = storedLanguage;
        return;
      }
      this.currentLanguage = defaultLanguage;
      this.$i18n.locale = defaultLanguage;
    },
    loadSupportedLocales() {
      const envLocales =
        process.env.VUE_APP_118N_SUPPORTED_LOCALES || 'pt-BR,en,es';

      const localesArray = envLocales.split(',').map((locale) => locale.trim());

      const localeMap = {
        'pt-BR': { value: 'pt-BR', label: 'Português', flagClass: 'flag__br' },
        en: { value: 'en', label: 'English', flagClass: 'flag__us' },
        es: { value: 'es', label: 'Español', flagClass: 'flag__es' },
      };

      this.supportedLocales = localesArray
        .filter((locale) => localeMap[locale])
        .map((locale) => ({
          ...localeMap[locale],
        }));
    },
  },
  mounted() {
    this.loadSupportedLocales();
    this.loadStoredLanguage();
  },
  beforeDestroy() {
    // Important: Clean up the event listener when the component is destroyed
    // to prevent memory leaks, just in case it's open during destruction.
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style scoped>
/* Main container - sets the positioning context */
.language-switcher-container {
  position: relative;
  display: inline-block;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Helvetica, Arial, sans-serif;
}

/* Main button that triggers the dropdown */
.language-switcher-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #fff;
  border: 1px solid #e2e8f0; /* Softer border color */
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%;
  min-width: 130px; /* Adjusted width */
  text-align: left;
}

.language-switcher-button:hover {
  border-color: #cbd5e0;
}

.language-switcher-button:focus,
.language-switcher-button.dropdown-open {
  outline: none;
  border-color: #a0aec0;
  box-shadow: 0 0 0 1px #a0aec0; /* More subtle focus ring */
}

.language-switcher-button:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
  opacity: 0.7;
}

.selected-language {
  display: flex;
  align-items: center;
}

/* Dropdown arrow, with rotation animation */
.dropdown-arrow {
  transition: transform 0.3s ease;
  color: #718096; /* Softer arrow color */
}

.language-switcher-button.dropdown-open .dropdown-arrow {
  transform: rotate(180deg);
}

/* Options list */
.language-options-list {
  position: absolute;
  top: calc(100% + 6px); /* Positions below the button with a small gap */
  right: 0;
  width: 100%;
  list-style: none;
  margin: 0;
  padding: 6px 0;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); /* Softer shadow */
  z-index: 1000; /* Ensures it's on top of other elements */
  overflow: hidden;
}

/* List items */
.language-option {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;
}

.language-option:hover {
  background-color: #f7fafc;
}

.language-option.is-selected {
  font-weight: 500;
  background-color: #edf2f7;
}

/* Fade transition for the dropdown */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Flag styles (maintained from original) */
.flag {
  display: inline-block;
  width: 24px;
  height: 16px;
  margin-right: 10px;
  background-image: url('https://cdn.assine.online/assets/images/flags.png');
  background-repeat: no-repeat;
  background-size: auto;
  border: 1px solid #e2e8f0;
  border-radius: 3px;
}

.language-label {
  vertical-align: middle;
  color: #4a5568; /* Softer text color */
  font-size: 14px;
  font-weight: 500;
}

.flag__br {
  background-position: -679px 0;
}
.flag__us {
  background-position: -5262px 1px;
}
.flag__es {
  background-position: -1554px 0;
}
</style>
